import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { Filter } from 'modules/core/components/DataTable/types'
import ClearFilters from 'modules/core/components/Filter/ClearFilters'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import AddFilter from './AddFilter'
import FilterSwitch from './FilterSwitch'

// Helper function to determine if a filter should auto-open its dropdown
function shouldFilterAutoOpen<T>(filter: Filter<T>, isNewFilter: boolean): boolean {
  // Don't auto-open if explicitly requested not to (set by chart interactions)
  if (filter.noAutoOpen === true) {
    return false
  }

  // Auto-open only for new filters (manual additions via +NewFilter)
  return isNewFilter
}

export default function Filters<T>() {
  const { filters, availableFilters, onResetFilters, enableClearFilters } = useFilters<T>()

  // Track previous filters to detect newly added ones
  const prevFiltersRef = React.useRef<Filter<T>[]>(filters)

  useUnsafeEffect(() => {
    prevFiltersRef.current = filters
  })

  if (filters.length === 0 && availableFilters.length === 0) return null

  const renderFilters = () => {
    return filters.map((dataTableFilter) => {
      const filterId = dataTableFilter.filter.identifier
      const isNewFilter = !prevFiltersRef.current.find(
        (filter) => filter.filter.identifier === filterId
      )

      const shouldOpen = shouldFilterAutoOpen(dataTableFilter, isNewFilter)

      // Create clean filter for component (remove noAutoOpen after evaluation)
      const cleanFilter =
        'noAutoOpen' in dataTableFilter
          ? (() => {
              const { noAutoOpen: _, ...clean } = dataTableFilter
              return clean
            })()
          : dataTableFilter

      return <FilterSwitch key={filterId} filter={cleanFilter} defaultOpen={shouldOpen} />
    })
  }

  const showClearFilters = () => {
    return (
      enableClearFilters &&
      filters.some((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.some((value) => value != undefined)
        }
        return filter.value != undefined
      })
    )
  }

  return (
    <Box flexDirection="row" gap="s8" overflowX="auto" width="full">
      <Box flexDirection="row" gap="s8" alignItems="flexStart" overflowX="auto" width="full">
        {renderFilters()}
        {availableFilters.length > 0 && <AddFilter />}
      </Box>
      {showClearFilters() && <ClearFilters onClick={onResetFilters} />}
    </Box>
  )
}
